// The classification strip (DESIGN-SYSTEM §00.3 over §10.1; shell-ia §10; simplified 15 Oct 2026, twice): on screen only when the highest
// level in view is Restricted or Confidential (the states that change how the person handles what they see), 22 px (20 on a phone), amber
// or red, above everything (z 80); an Internal or Public screen carries no strip at all, and every printed page keeps the marking whatever
// the level. The surface carries only the bilingual mark with a quiet ⓘ. Hovering the mark gives the handling rule; clicking it opens
// what raised the level, the handling rule, the Ministry's policy line in English and Arabic, what is withheld from AI at this level
// (SPEC §21.1) and the links to the policy and the Trust Centre. The environment off production sits in the toolbar. WP-SHELL.
import { useLayoutEffect } from "react";
import { ChevronRight, Info, Lock, ShieldCheck } from "lucide-react";
import { Link } from "react-router";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Popover, PopoverContent, PopoverTrigger, Tooltip } from "@ncp/ui";
import { SENSITIVITY_NAMES } from "../theme/primitives";
import { useCurrentClassification, type Sensitivity } from "./classification";
import { MARK_EN, POLICY_LINE } from "./marking";
import { aiRouteSuffix, useAiRoute } from "./useShellSummary";

export function ClassificationBanner() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const { level, sources } = useCurrentClassification();
  const locked = level === "restricted" || level === "confidential";
  const nameOf = (l: Sensitivity) => (ar ? SENSITIVITY_NAMES[l].ar : SENSITIVITY_NAMES[l].en);
  // "Zero retention" only where the server enforces it (Llm:RequireZeroDataRetention); otherwise what actually happens.
  const aiRoute = useAiRoute();
  const route = level === "internal" ? aiRouteSuffix(aiRoute) : "";
  const withheld = t(`assistant:econic.shell.class.withheld.${level}${route}`);
  const handling = t(`assistant:econic.shell.class.handling.${level}${route}`);
  // The strip takes its height in the chrome only while it is on screen (shell.css: html[data-mark] → --econic-banner-h).
  useLayoutEffect(() => {
    const html = document.documentElement;
    if (locked) html.dataset.mark = level;
    else delete html.dataset.mark;
    return () => {
      delete html.dataset.mark;
    };
  }, [locked, level]);

  return (
    <div className="es-banner" role="note" aria-label={t("assistant:econic.shell.class.label")} data-classification={level} data-econic-banner="" data-quiet={locked ? undefined : ""}>
      <Popover>
        <Tooltip content={handling} side="bottom">
          <PopoverTrigger asChild>
            <button type="button" className="mk" aria-label={t("assistant:econic.shell.class.highest", { level: nameOf(level), source: sources[0]?.source ?? "" })}>
              {locked ? <Lock aria-hidden /> : null}
              <span className="lv" lang="en">{MARK_EN[level]}</span>
              <span aria-hidden>·</span>
              <span className="ar" lang="ar">{SENSITIVITY_NAMES[level].ar}</span>
              <Info aria-hidden className="i" />
            </button>
          </PopoverTrigger>
        </Tooltip>
        <PopoverContent align="start" sideOffset={6} className="es-pop es-banner-pop w-[340px] max-w-[calc(100vw-24px)]">
          <p className="t">{t("assistant:econic.shell.class.popTitle", { level: nameOf(level) })}</p>
          <ul>
            {sources.map((s, i) => (
              <li key={`${s.level}-${i}`}>
                <span className={`ec-cls ${s.level}`} style={{ flex: "none" }}>{nameOf(s.level)}</span>
                <span>{s.source || t("assistant:econic.shell.class.floor")}</span>
              </li>
            ))}
          </ul>
          <p className="note">{handling}</p>
          <p className="note pl"><span lang="en">{POLICY_LINE.en}</span><span lang="ar">{POLICY_LINE.ar}</span></p>
          <div className="lnks">
            <Link to="/trust/security" className="lnk">
              {t("assistant:econic.shell.class.policy")}
              <ChevronRight className="rtl:-scale-x-100" aria-hidden />
            </Link>
            <Link to="/trust" className="lnk" aria-label={t("assistant:econic.shell.class.whLink", { text: withheld })} data-econic-trust="">
              <ShieldCheck aria-hidden className="sh" />
              {withheld}
              <ChevronRight className="rtl:-scale-x-100" aria-hidden />
            </Link>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
